// frontend/src/features/landingPages/LandingPageBuilderView.js
//
// M12 — Landing Pages & Conversion (FR-M12-001, LP-1/LP-2)
// Landing page outline builder backed by /api/v1/landing-pages.
//
//  - Pages, versions and approvals are stored on the server (workspace-scoped).
//  - The server decides permissions; this view only reflects `access`
//    (owner/admin/sales manager approve; members build and submit for review).
//  - If the server feature flag (LANDING_PAGES_V1) is off, the view falls back
//    to the earlier browser-only prototype (localStorage) so nothing breaks.
//  - Approved versions are published to the workspace CMS (owner/admin/manager);
//    the live URL is what articles link to. Form capture is a later increment.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Banner from '../../ui/Banner';
import SectionPalette from './components/SectionPalette';
import SectionOutlineItem from './components/SectionOutlineItem';
import StructurePreview from './components/StructurePreview';
import LpCustomSelect from './components/LpCustomSelect';
import { createSection, getSectionType } from './sectionCatalog';
import landingPagesApi, {
  toApiError,
  isFeatureDisabled,
  newIdempotencyKey,
} from './api/landingPagesApi';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import './LandingPageBuilderView.css';

export const STORAGE_KEY = 'lead_huntrix_lp_outline_prototype';
const NEW_PAGE = '__new__';

const STATUS_LABELS = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  rejected: 'Rejected',
  superseded: 'Superseded',
};

function loadLocalState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        pageTitle: typeof parsed.pageTitle === 'string' ? parsed.pageTitle : '',
        sections: Array.isArray(parsed.sections) ? parsed.sections : [],
        device: parsed.device === 'phone' ? 'phone' : 'desktop',
        theme: parsed.theme === 'light' ? 'light' : 'dark',
      };
    }
  } catch {
    /* ignore corrupt prototype state */
  }
  return { pageTitle: '', sections: [], device: 'desktop', theme: 'dark' };
}

/** Server sections ({id,type,content}) -> UI sections (adds catalog label). */
function fromServerSections(sections) {
  return (Array.isArray(sections) ? sections : []).map((s) => ({
    id: s.id,
    type: s.type,
    label: getSectionType(s.type)?.label || s.type,
    content: { ...(s.content || {}) },
  }));
}

/** UI sections -> payload; drop empty values so optional fields stay optional. */
function toServerSections(sections) {
  return sections.map((s) => ({
    id: s.id,
    type: s.type,
    content: Object.fromEntries(
      Object.entries(s.content || {}).filter(([, v]) => typeof v === 'string' && v !== '')
    ),
  }));
}

function snapshot(name, sections) {
  return JSON.stringify({ name: name.trim(), sections: toServerSections(sections) });
}

function describeError(apiError) {
  if (!apiError) return null;
  const first = apiError.details?.[0];
  const detail = first ? ` (${first.path}: ${first.message})` : '';
  const ref = apiError.requestId ? ` Reference: ${apiError.requestId}` : '';
  return `${apiError.message}${detail}${ref}`;
}

export default function LandingPageBuilderView() {
  const { workspaceId, isPersonalWorkspace } = useWorkspace() || {};
  const initial = useMemo(loadLocalState, []);

  // When opened from the Content Studio, offer a one-click return that reopens
  // the originating article's studio.
  const navigate = useNavigate();
  const location = useLocation();
  const returnToStudio =
    location.state?.returnTo === '/seo-aieo' && location.state?.briefId
      ? { path: '/seo-aieo', briefId: location.state.briefId }
      : null;
  const handleReturnToStudio = () => {
    if (!returnToStudio) return;
    navigate(returnToStudio.path, { state: { openBriefId: returnToStudio.briefId } });
  };

  const [mode, setMode] = useState('loading'); // 'loading' | 'server' | 'local'
  const [pages, setPages] = useState([]);
  const [access, setAccess] = useState({ canView: false, canEdit: false, canApprove: false });
  const [selectedId, setSelectedId] = useState(NEW_PAGE);
  const [page, setPage] = useState(null); // server page detail
  const [pageTitle, setPageTitle] = useState('');
  const [sections, setSections] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState(snapshot('', []));
  const [device, setDevice] = useState(initial.device);
  const [theme, setTheme] = useState(initial.theme);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'error'|'success'|'info', text }
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [serverPreview, setServerPreview] = useState(null);
  const [members, setMembers] = useState([]);

  const isServer = mode === 'server';
  const canEdit = !isServer || access.canEdit;
  const canApprove = isServer && access.canApprove;
  const canPublish = isServer && access.canPublish;
  const draft = page?.currentDraft || null;
  const dirty = snapshot(pageTitle, sections) !== savedSnapshot;
  const localOutline = initial.sections.length > 0 ? initial : null;

  const applyPage = useCallback((detail) => {
    setPage(detail);
    const name = detail?.name || '';
    const secs = fromServerSections(detail?.currentDraft?.sections);
    setPageTitle(name);
    setSections(secs);
    setSavedSnapshot(snapshot(name, secs));
    setServerPreview(null);
    setShowReject(false);
    setRejectReason('');
  }, []);

  const startNewPage = useCallback(() => {
    setSelectedId(NEW_PAGE);
    setPage(null);
    setPageTitle('');
    setSections([]);
    setSavedSnapshot(snapshot('', []));
    setServerPreview(null);
  }, []);

  // Initial load: list pages; fall back to local mode if the feature is disabled.
  const loadList = useCallback(async () => {
    try {
      const data = await landingPagesApi.list(workspaceId);
      setPages(data?.items || []);
      setAccess(data?.access || { canView: true, canEdit: false, canApprove: false });
      setMode('server');
      return data?.items || [];
    } catch (error) {
      const apiError = toApiError(error);
      if (isFeatureDisabled(apiError)) {
        setMode('local');
        setPageTitle(initial.pageTitle);
        setSections(initial.sections);
        return null; // local mode: keep the restored browser outline
      }
      setMode('server');
      setMessage({ type: 'error', text: describeError(apiError) });
      return [];
    }
  }, [workspaceId, initial]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMode('loading');
      const items = await loadList();
      if (cancelled || items === null) return;
      if (items.length > 0) {
        setSelectedId(items[0].id);
      } else {
        startNewPage();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadList, startNewPage]);

  // Load the selected page detail.
  useEffect(() => {
    if (!isServer || selectedId === NEW_PAGE) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const detail = await landingPagesApi.get(workspaceId, selectedId);
        if (cancelled) return;
        applyPage(detail);
        if (detail?.access) setAccess(detail.access);
      } catch (error) {
        if (!cancelled) setMessage({ type: 'error', text: describeError(toApiError(error)) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isServer, selectedId, workspaceId, applyPage]);

  // Members for the lead owner picker (organization workspaces, publishers only).
  useEffect(() => {
    if (!isServer || !access.canPublish || isPersonalWorkspace || !workspaceId) return undefined;
    let cancelled = false;
    landingPagesApi
      .members(workspaceId)
      .then((list) => {
        if (!cancelled) setMembers(list.filter((m) => m.user_id && m.is_active !== false));
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isServer, access.canPublish, isPersonalWorkspace, workspaceId]);

  // Local (flag-off) mode keeps the original browser-only persistence.
  useEffect(() => {
    if (mode !== 'local') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ pageTitle, sections, device, theme }));
    } catch {
      /* storage may be unavailable */
    }
  }, [mode, pageTitle, sections, device, theme]);

  // ── Outline editing ───────────────────────────────────────────────────────
  const handleAddSection = (type) => {
    const section = createSection(type);
    if (section) setSections((prev) => [...prev, section]);
  };
  const handleChangeContent = (id, content) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, content } : s)));
  };
  const handleMove = (index, direction) => {
    setSections((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };
  const handleRemove = (id) => setSections((prev) => prev.filter((s) => s.id !== id));
  const handleClear = () => {
    if (sections.length === 0) return;
    // eslint-disable-next-line no-alert
    if (window.confirm('Clear the entire outline? Unsaved changes will be lost.')) {
      setSections([]);
    }
  };

  // ── Server actions ────────────────────────────────────────────────────────
  const run = async (fn, successText) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      if (successText) setMessage({ type: 'success', text: successText });
    } catch (error) {
      const apiError = toApiError(error);
      const text =
        apiError.status === 409 && apiError.code === 'LP_VERSION_CONFLICT'
          ? `${apiError.message} Use "Reload" to get the latest version.`
          : describeError(apiError);
      setMessage({ type: 'error', text });
    } finally {
      setBusy(false);
    }
  };

  const refreshAfter = async (detail) => {
    const items = await loadList();
    setSelectedId(detail.id);
    const fresh = await landingPagesApi.get(workspaceId, detail.id);
    applyPage(fresh);
    if (fresh?.access) setAccess(fresh.access);
    return items;
  };

  const handleSave = () => {
    if (!pageTitle.trim()) {
      setMessage({ type: 'error', text: 'Enter a page name before saving.' });
      return Promise.resolve();
    }
    return run(async () => {
      const payload = { name: pageTitle.trim(), sections: toServerSections(sections) };
      const detail =
        selectedId === NEW_PAGE
          ? await landingPagesApi.create(workspaceId, payload)
          : await landingPagesApi.saveDraft(workspaceId, selectedId, payload, page?.rowVersion);
      await refreshAfter(detail);
    }, 'Draft saved.');
  };

  const handleImportLocal = () =>
    run(async () => {
      const detail = await landingPagesApi.create(workspaceId, {
        name: localOutline.pageTitle.trim() || 'Imported landing page',
        sections: toServerSections(localOutline.sections),
      });
      await refreshAfter(detail);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }, 'Outline from this browser imported as a new draft.');

  const handleSubmit = () =>
    run(async () => {
      const detail = await landingPagesApi.submit(workspaceId, page.id, draft.id, page.rowVersion);
      await refreshAfter(detail);
    }, 'Submitted for review. An owner, admin or sales manager can now approve it.');

  const handleSubmitAndApprove = () =>
    run(async () => {
      const submitted = await landingPagesApi.submit(
        workspaceId,
        page.id,
        draft.id,
        page.rowVersion
      );
      const v = submitted.currentDraft;
      try {
        const detail = await landingPagesApi.approve(workspaceId, page.id, v.id, v.contentHash);
        await refreshAfter(detail);
      } catch (error) {
        // Submitted but not approved: show the in-review state, then surface the error.
        await refreshAfter(submitted).catch(() => {});
        throw error;
      }
    }, 'Approved. This version is now the approved landing page.');

  const handleApprove = () =>
    run(async () => {
      const detail = await landingPagesApi.approve(
        workspaceId,
        page.id,
        draft.id,
        draft.contentHash
      );
      await refreshAfter(detail);
    }, 'Approved. This version is now the approved landing page.');

  const handleReject = () =>
    run(async () => {
      const detail = await landingPagesApi.reject(
        workspaceId,
        page.id,
        draft.id,
        draft.contentHash,
        rejectReason.trim()
      );
      await refreshAfter(detail);
    }, 'Rejected. The author can edit and resubmit.');

  const handlePublish = () =>
    run(async () => {
      await landingPagesApi.publish(workspaceId, page.id, newIdempotencyKey());
      await refreshAfter(page);
    }, 'Published to the CMS. Articles can now link to this landing page.');

  const handleRollback = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Roll back the CMS page to the previously published version?')) {
      return Promise.resolve();
    }
    return run(async () => {
      await landingPagesApi.rollback(workspaceId, page.id, newIdempotencyKey());
      await refreshAfter(page);
    }, 'Rolled back. The CMS page now shows the previously published version.');
  };

  const handleLeadOwnerChange = (leadOwnerId) =>
    run(async () => {
      await landingPagesApi.updateSettings(workspaceId, page.id, { leadOwnerId });
      const fresh = await landingPagesApi.get(workspaceId, page.id);
      setPage(fresh);
    }, 'Lead owner updated. New form leads from this page go to this person.');

  const handleReload = () =>
    run(async () => {
      const fresh = await landingPagesApi.get(workspaceId, page.id);
      applyPage(fresh);
    });

  const handleServerPreview = () =>
    run(async () => {
      const html = await landingPagesApi.previewHtml(workspaceId, page.id, draft.id);
      setServerPreview(html);
    });

  // ── Derived UI state ──────────────────────────────────────────────────────
  const draftStatus = draft?.status;
  const approvedNumber = page?.approvedVersion?.versionNumber;
  const live = page?.livePublication || null;
  const liveUrl = live && /^https?:\/\//i.test(live.url || '') ? live.url : null;
  const liveNumber = page?.versions?.find((v) => v.id === live?.versionId)?.versionNumber;
  const lastAttempt = page?.publications?.[0] || null;
  const approvedIsLive = Boolean(live && live.versionId === page?.approvedVersionId);
  const hasLeadForm = sections.some((s) => s.type === 'leadForm');
  const submissions = page?.submissions || null;
  const ownerName = (m) => m.username || m.display_name || m.email || m.user_id;
  const leadOwnerOptions = useMemo(() => {
    const list = [
      {
        value: '',
        label: 'Page creator (default)',
        subtitle: 'Submissions assigned to page creator',
        icon: 'fas fa-user-shield',
      },
    ];
    if (Array.isArray(members)) {
      members.forEach((m) => {
        list.push({
          value: m.user_id,
          label: ownerName(m),
          subtitle: m.email && m.email !== ownerName(m) ? m.email : 'Workspace team member',
          icon: 'fas fa-user',
        });
      });
    }
    return list;
  }, [members]);

  const pageOptions = useMemo(() => {
    const list = pages.map((p) => ({
      value: p.id,
      label: p.name,
      subtitle: p.approvedVersion
        ? `v${p.approvedVersion.versionNumber} approved`
        : 'Draft outline',
      icon: 'fas fa-file-lines',
    }));
    if (canEdit) {
      list.push({
        value: NEW_PAGE,
        label: '+ New landing page',
        subtitle: 'Start a blank landing page',
        icon: 'fas fa-circle-plus',
      });
    }
    return list;
  }, [pages, canEdit]);

  const handlePageSelectChange = (val) => {
    if (dirty) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm('Discard unsaved changes?');
      if (!ok) return;
    }
    if (val === NEW_PAGE) startNewPage();
    else setSelectedId(val);
  };
  const editWarning =
    isServer && dirty && draftStatus === 'in_review'
      ? 'Saving will withdraw the version under review and start a new draft.'
      : isServer && dirty && (draftStatus === 'approved' || draftStatus === 'rejected')
        ? 'Saving creates a new draft. The approved version stays in place until the new draft is approved.'
        : null;

  if (mode === 'loading') {
    return (
      <div className="lp-view">
        <p className="lp-status-line" role="status">
          Loading landing pages…
        </p>
      </div>
    );
  }

  return (
    <div className="lp-view">
      <Banner
        title="Landing Page Builder"
        subtitle="Structure the outline of a landing page, save drafts, and send them for approval. Hosting and publishing come in a later phase."
        type="page-header"
        actions={
          <div className="lp-header-actions">
            {returnToStudio && (
              <button
                type="button"
                className="lp-btn lp-btn-primary"
                onClick={handleReturnToStudio}
                data-testid="lp-back-to-studio-btn"
                title="Return to the Content Studio article you came from"
              >
                <i className="fas fa-arrow-left" aria-hidden="true"></i>
                <span>Back to article</span>
              </button>
            )}
            {isServer ? (
              draft && (
                <span className={`lp-status-badge lp-status-${draftStatus}`}>
                  v{draft.versionNumber} · {STATUS_LABELS[draftStatus] || draftStatus}
                </span>
              )
            ) : (
              <span className="lp-prototype-badge" title="Server saving is not enabled">
                <i className="fas fa-flask" aria-hidden="true"></i>
                Prototype · saved in this browser only
              </span>
            )}
            <button
              type="button"
              className="lp-btn lp-btn-secondary"
              onClick={handleClear}
              disabled={sections.length === 0 || !canEdit || busy}
            >
              <i className="fas fa-eraser" aria-hidden="true"></i>
              <span>Clear outline</span>
            </button>
          </div>
        }
      />

      {message && (
        <div
          className={`lp-message lp-message-${message.type}`}
          role={message.type === 'error' ? 'alert' : 'status'}
        >
          <div className="lp-message-content">
            <i
              className={`fas ${
                message.type === 'error'
                  ? 'fa-circle-exclamation'
                  : message.type === 'success'
                    ? 'fa-circle-check'
                    : 'fa-circle-info'
              } lp-message-icon`}
              aria-hidden="true"
            ></i>
            <span>{message.text}</span>
          </div>
          <button
            type="button"
            className="lp-icon-btn"
            onClick={() => setMessage(null)}
            aria-label="Dismiss message"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
      )}

      {isServer && (
        <div className="lp-toolbar-card">
          <div className="lp-toolbar-header">
            <div className="lp-toolbar-row">
              <label className="lp-field-label" htmlFor="lp-page-select">
                <i className="fas fa-file-lines lp-label-icon" aria-hidden="true"></i>
                Landing page
              </label>
              <div className="lp-select-reload-group">
                <LpCustomSelect
                  id="lp-page-select"
                  value={selectedId}
                  options={pageOptions}
                  disabled={busy}
                  onChange={handlePageSelectChange}
                  placeholder="Select landing page..."
                  defaultIcon="fas fa-file-lines"
                />
                {page && (
                  <button
                    type="button"
                    className="lp-btn lp-btn-secondary lp-reload-btn"
                    onClick={handleReload}
                    disabled={busy}
                    title="Reload landing page from server"
                  >
                    <i className="fas fa-rotate" aria-hidden="true"></i>
                    <span>Reload</span>
                  </button>
                )}
              </div>
            </div>

            <div className="lp-action-row">
              {canEdit && (
                <button
                  type="button"
                  className="lp-btn lp-btn-primary"
                  onClick={handleSave}
                  disabled={busy || (!dirty && selectedId !== NEW_PAGE)}
                >
                  <i className="fas fa-floppy-disk" aria-hidden="true"></i>
                  <span>{selectedId === NEW_PAGE ? 'Create draft' : 'Save draft'}</span>
                </button>
              )}

              {canEdit && draftStatus === 'draft' && !canApprove && (
                <button
                  type="button"
                  className="lp-btn lp-btn-secondary"
                  onClick={handleSubmit}
                  disabled={busy || dirty || sections.length === 0}
                  title={dirty ? 'Save your changes first' : undefined}
                >
                  <i className="fas fa-paper-plane" aria-hidden="true"></i>
                  <span>Submit for review</span>
                </button>
              )}

              {canApprove && draftStatus === 'draft' && (
                <button
                  type="button"
                  className="lp-btn lp-btn-secondary"
                  onClick={handleSubmitAndApprove}
                  disabled={busy || dirty || sections.length === 0}
                  title={dirty ? 'Save your changes first' : undefined}
                >
                  <i className="fas fa-circle-check" aria-hidden="true"></i>
                  <span>Approve</span>
                </button>
              )}

              {canApprove && draftStatus === 'in_review' && (
                <>
                  <button
                    type="button"
                    className="lp-btn lp-btn-primary"
                    onClick={handleApprove}
                    disabled={busy || dirty}
                  >
                    <i className="fas fa-circle-check" aria-hidden="true"></i>
                    <span>Approve v{draft.versionNumber}</span>
                  </button>
                  <button
                    type="button"
                    className="lp-btn lp-btn-secondary"
                    onClick={() => setShowReject((v) => !v)}
                    disabled={busy || dirty}
                    aria-expanded={showReject}
                  >
                    <i className="fas fa-circle-xmark" aria-hidden="true"></i>
                    <span>Reject</span>
                  </button>
                </>
              )}

              {draft && (
                <button
                  type="button"
                  className="lp-btn lp-btn-secondary"
                  onClick={handleServerPreview}
                  disabled={busy || dirty}
                  title={dirty ? 'Save your changes to preview them' : 'Rendered by the server'}
                >
                  <i className="fas fa-eye" aria-hidden="true"></i>
                  <span>Server preview</span>
                </button>
              )}
            </div>
          </div>

          <div className="lp-status-overview">
            {page && (
              <p className="lp-status-line">
                {approvedNumber
                  ? `Approved version: v${approvedNumber}.`
                  : 'No version has been approved yet.'}{' '}
                {draft &&
                  draftStatus !== 'approved' &&
                  `Working version: v${draft.versionNumber} (${STATUS_LABELS[draftStatus] || draftStatus}).`}
              </p>
            )}

            {!canApprove && draftStatus === 'in_review' && (
              <span className="lp-status-line lp-status-in-review-note">
                <i className="fas fa-clock" aria-hidden="true"></i>
                Waiting for an owner, admin or sales manager to review.
              </span>
            )}

            {!canEdit && (
              <p className="lp-status-line lp-status-readonly-note">
                <i className="fas fa-lock" aria-hidden="true"></i>
                Your role can view landing pages but not edit them.
              </p>
            )}

            {canEdit && localOutline && selectedId === NEW_PAGE && (
              <p className="lp-status-line lp-status-import-note">
                <i className="fas fa-file-import" aria-hidden="true"></i>
                An outline is saved in this browser from the earlier prototype.{' '}
                <button
                  type="button"
                  className="lp-link-btn"
                  onClick={handleImportLocal}
                  disabled={busy}
                >
                  Import it as a new draft
                </button>
              </p>
            )}

            {draftStatus === 'rejected' &&
              page?.approvals?.[0]?.decision === 'rejected' &&
              page.approvals[0].versionId === draft.id && (
                <p className="lp-status-line lp-status-rejected-note">
                  <i className="fas fa-circle-xmark" aria-hidden="true"></i>
                  Rejected: {page.approvals[0].reason}
                </p>
              )}

            {editWarning && (
              <p className="lp-status-line lp-status-warning">
                <i className="fas fa-triangle-exclamation" aria-hidden="true"></i>
                {editWarning}
              </p>
            )}
          </div>

          {showReject && canApprove && draftStatus === 'in_review' && (
            <div className="lp-reject-box">
              <label className="lp-field-label" htmlFor="lp-reject-reason">
                <i className="fas fa-triangle-exclamation" aria-hidden="true"></i>
                Reason for rejection (shown to the author)
              </label>
              <textarea
                id="lp-reject-reason"
                className="lp-field-input"
                rows={3}
                maxLength={1000}
                placeholder="Explain what needs to be changed before this version can be approved..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <button
                type="button"
                className="lp-btn lp-btn-danger"
                onClick={handleReject}
                disabled={busy || !rejectReason.trim()}
              >
                Confirm rejection
              </button>
            </div>
          )}

          {page?.approvedVersion && (
            <div className="lp-publish-box" aria-label="CMS publishing">
              <div className="lp-publish-box-content">
                <span className="lp-box-icon" aria-hidden="true">
                  <i className="fas fa-cloud-arrow-up"></i>
                </span>
                <div className="lp-box-main">
                  <div className="lp-box-header">
                    <span className="lp-box-title">CMS Publishing</span>
                    <span className={`lp-live-pill ${liveUrl ? 'live' : 'unpublished'}`}>
                      <span className="lp-status-dot" />
                      {liveUrl ? 'Live on CMS' : 'Unpublished'}
                    </span>
                  </div>
                  <p className="lp-status-line">
                    {liveUrl ? (
                      <>
                        Live on CMS{liveNumber ? ` (v${liveNumber})` : ''}:{' '}
                        <a href={liveUrl} target="_blank" rel="noopener noreferrer">
                          {liveUrl}
                        </a>
                        {live.isSimulated ? ' · simulated sandbox' : ''}
                      </>
                    ) : (
                      'Not published to the CMS yet. Articles can only link to a published landing page.'
                    )}
                  </p>
                  {lastAttempt?.status === 'failed' && (
                    <p className="lp-status-line lp-status-rejected-note">
                      <i className="fas fa-circle-exclamation" aria-hidden="true"></i>
                      Last publish attempt failed ({lastAttempt.errorCode || 'unknown error'}).
                    </p>
                  )}
                </div>
              </div>
              {canPublish && (
                <div className="lp-action-row lp-publish-actions">
                  <button
                    type="button"
                    className="lp-btn lp-btn-primary"
                    onClick={handlePublish}
                    disabled={busy}
                  >
                    <i className="fas fa-cloud-arrow-up" aria-hidden="true"></i>
                    <span>
                      {!live
                        ? `Publish v${approvedNumber} to CMS`
                        : approvedIsLive
                          ? 'Republish to CMS'
                          : `Publish v${approvedNumber} to CMS`}
                    </span>
                  </button>
                  {page.canRollback && (
                    <button
                      type="button"
                      className="lp-btn lp-btn-secondary"
                      onClick={handleRollback}
                      disabled={busy}
                    >
                      <i className="fas fa-clock-rotate-left" aria-hidden="true"></i>
                      <span>Roll back</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {page && hasLeadForm && (
            <div className="lp-publish-box lp-leads-box" aria-label="Form leads">
              <div className="lp-publish-box-content">
                <span className="lp-box-icon" aria-hidden="true">
                  <i className="fas fa-inbox"></i>
                </span>
                <div className="lp-box-main">
                  <div className="lp-box-header">
                    <span className="lp-box-title">Form Leads &amp; Routing</span>
                  </div>
                  {isPersonalWorkspace ? (
                    <p className="lp-status-line">Form submissions become leads assigned to you.</p>
                  ) : canPublish && members.length > 0 ? (
                    <div className="lp-toolbar-row lp-lead-owner-group">
                      <label className="lp-field-label" htmlFor="lp-lead-owner">
                        Lead owner
                      </label>
                      <LpCustomSelect
                        id="lp-lead-owner"
                        value={page.leadOwnerId || ''}
                        options={leadOwnerOptions}
                        disabled={busy}
                        onChange={handleLeadOwnerChange}
                        placeholder="Select lead owner..."
                        defaultIcon="fas fa-user"
                      />
                    </div>
                  ) : (
                    <p className="lp-status-line">
                      Form leads go to {page.leadOwner?.username || 'the page creator'}
                      {page.leadOwner?.isDefault ? ' (page creator)' : ''}.
                    </p>
                  )}
                  {submissions && submissions.total > 0 && (
                    <p className="lp-status-line" data-testid="lp-submission-summary">
                      {submissions.total} form submission{submissions.total === 1 ? '' : 's'}:{' '}
                      {submissions.leadsCreated} new lead{submissions.leadsCreated === 1 ? '' : 's'}
                      , {submissions.leadsUpdated} existing
                      {submissions.pending ? `, ${submissions.pending} processing` : ''}
                      {submissions.failed ? `, ${submissions.failed} failed` : ''}.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="lp-title-card">
        <div className="lp-title-card-inner">
          <span className="lp-title-icon" aria-hidden="true">
            <i className="fas fa-file-pen"></i>
          </span>
          <div className="lp-title-input-group">
            <label className="lp-field-label" htmlFor="lp-page-title">
              Page name
            </label>
            <input
              id="lp-page-title"
              type="text"
              className="lp-field-input lp-title-input"
              placeholder="e.g. Spring Demo Request"
              maxLength={120}
              value={pageTitle}
              disabled={!canEdit || busy}
              onChange={(e) => setPageTitle(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="lp-workbench lp-workbench-stacked">
        {/* Top: Live Preview or Server Preview */}
        <div className="lp-workbench-preview-top">
          {serverPreview !== null ? (
            <section className="lp-server-preview" aria-label="Server preview">
              <div className="lp-outline-header">
                <div className="lp-outline-title-wrap">
                  <span className="lp-outline-header-icon" aria-hidden="true">
                    <i className="fas fa-eye"></i>
                  </span>
                  <h2 className="lp-outline-title">Server preview · v{draft?.versionNumber}</h2>
                </div>
                <button
                  type="button"
                  className="lp-btn lp-btn-secondary"
                  onClick={() => setServerPreview(null)}
                >
                  <i className="fas fa-arrow-left" aria-hidden="true"></i>
                  <span>Back to live preview</span>
                </button>
              </div>
              <iframe
                title="Server-rendered landing page preview"
                className="lp-server-preview-frame"
                sandbox=""
                srcDoc={serverPreview}
              />
            </section>
          ) : (
            <StructurePreview
              pageTitle={pageTitle}
              sections={sections}
              device={device}
              theme={theme}
              onDeviceChange={setDevice}
              onThemeChange={setTheme}
            />
          )}
        </div>

        {/* Bottom: Unified Section Builder Workbench */}
        <section className="lp-outline lp-structure-builder" aria-label="Page outline">
          <div className="lp-outline-header">
            <div className="lp-outline-title-wrap">
              <span className="lp-outline-header-icon" aria-hidden="true">
                <i className="fas fa-layer-group"></i>
              </span>
              <div className="lp-outline-titles">
                <h2 className="lp-outline-title">Page outline</h2>
                <span className="lp-outline-subtitle">
                  Click any block below to append it to your page outline, then reorder or edit
                  details.
                </span>
              </div>
            </div>
            <span className="lp-outline-count">
              {sections.length} section{sections.length === 1 ? '' : 's'}
              {isServer && dirty ? ' · unsaved changes' : ''}
            </span>
          </div>

          {/* Quick-Add Toolbar Ribbon */}
          <fieldset className="lp-fieldset-reset" disabled={!canEdit || busy}>
            <legend className="lp-visually-hidden">Section blocks</legend>
            <SectionPalette onAddSection={handleAddSection} disabled={!canEdit || busy} />
          </fieldset>

          {/* Active Outline Section List */}
          <fieldset className="lp-fieldset-reset" disabled={!canEdit || busy}>
            <legend className="lp-visually-hidden">Outline sections</legend>
            {sections.length === 0 ? (
              <div className="lp-outline-empty">
                <i className="fas fa-cubes-stacked" aria-hidden="true"></i>
                <p className="lp-outline-empty-title">Your landing page outline is empty</p>
                <p className="lp-outline-empty-desc">
                  Click any block in the bar above (such as <strong>Hero</strong> or{' '}
                  <strong>Lead Capture Form</strong>) to start shaping your page structure.
                </p>
              </div>
            ) : (
              <ul className="lp-outline-list">
                {sections.map((section, index) => (
                  <SectionOutlineItem
                    key={section.id}
                    section={section}
                    index={index}
                    total={sections.length}
                    onChangeContent={handleChangeContent}
                    onMoveUp={(i) => handleMove(i, -1)}
                    onMoveDown={(i) => handleMove(i, 1)}
                    onRemove={handleRemove}
                  />
                ))}
              </ul>
            )}
          </fieldset>
        </section>
      </div>
    </div>
  );
}
